/**
 * F41 (Wave 2 Track D — admin) — admin tournament moderation API client.
 *
 * Endpoints (registered under `prefix: '/admin'` — see
 * apps/api/src/routes/admin/tournaments.ts + public-tournaments.ts):
 *   GET    /admin/tournaments                          → listTournaments (paginated; status / suspended / organizer filters)
 *   GET    /admin/tournaments/analytics                → getAnalytics (platform rollup)
 *   GET    /admin/tournaments/:id                       → getTournament (detail = TournamentDetail)
 *   PATCH  /admin/tournaments/:id                       → editTournament (title + description only — the
 *                                                          standard organizer field-lock still applies;
 *                                                          a true bypass is the post-MVP MN2 follow-up;
 *                                                          `tournaments:moderate`)
 *   POST   /admin/tournaments/:id/suspend               → suspendTournament (`tournaments:moderate`)
 *   POST   /admin/tournaments/:id/unsuspend             → unsuspendTournament (`tournaments:moderate`)
 *   POST   /admin/tournaments/:id/cancel                → cancelTournament (`tournaments:moderate`)
 *   POST   /admin/tournaments/:id/force-refund          → forceRefund (`tournaments:moderate`)
 *   POST   /admin/organizers/:b2bAccountId/approve      → approveOrganizer (`tournaments:approve_organizer`)
 *   POST   /admin/organizers/:b2bAccountId/suspend      → suspendOrganizer (`tournaments:approve_organizer`)
 *   GET    /admin/tournaments/:id/bracket                → getBracket (admin live read — bypasses the
 *                                                          invite-only viewer gate; one BracketView per division)
 *   GET    /admin/tournaments/:id/announcements          → listAnnouncements (every announcement, hidden
 *                                                          ones included with their hide stamp — FIX 5.7)
 *   POST   /admin/tournament-announcements/:id/hide      → hideAnnouncement (mandatory audited reason;
 *                                                          `tournaments:moderate`)
 *   POST   /admin/tournaments                            → createTournament (admin-create on behalf of an
 *                                                          organizer — D19; `tournaments:create`)
 *   POST   /admin/tournaments/:id/matches/:matchId/score     → enterScore     (`tournaments:enter_scores`)
 *   POST   /admin/tournaments/:id/matches/:matchId/finalize  → finalizeMatch  (`tournaments:enter_scores`)
 *   POST   /admin/tournaments/:id/matches/:matchId/correct   → correctScore   (`tournaments:enter_scores`)
 *   POST   /admin/tournaments/:id/divisions/:divId/seed      → seedDivision   (`tournaments:manage_bracket`)
 *   POST   /admin/tournaments/:id/divisions/:divId/bracket   → generateBracket (`tournaments:manage_bracket`)
 *
 * Permission gating happens server-side. The page-level <ProtectedRoute> gates
 * which routes a given admin role can navigate to; the in-page action bars gate
 * the moderation buttons on the actor's permission set. The API client forwards
 * requests and relies on the server to enforce RBAC.
 *
 * ⚠️ Backend-contract notes (verified against the real routes):
 *   - The admin tournament list returns the RAW snake_case `tournaments` rows
 *     (`SELECT *`), NOT the camelCase `TournamentSummary` shape. This client maps
 *     the subset the list page needs into a stable `AdminTournamentRow`.
 *   - `GET /admin/tournaments/:id/registrants` returns the real roster
 *     (`RegistrantRosterEntry[]` — each registration joined to its division name +
 *     solo user display name/email OR team name). The detail page renders it.
 *   - `GET /admin/organizers` is the pending-approval queue (paginated
 *     `OrganizerProfileWithAccount[]` joined to the b2b_account legal/display
 *     name). The organizers page is a real queue + approve/suspend surface.
 *
 * Component tests get the in-memory mock via the vitest alias in
 * apps/admin/vitest.config.ts rewriting `'./api/tournaments'`.
 */
import type {
  AdminAnnouncementItem,
  AdminCreateTournamentInput,
  ApproveOrganizerInput,
  BracketView,
  CancelTournamentInput,
  CorrectMatchScoreInput,
  EnterMatchScoreInput,
  FinalizeMatchInput,
  ForceRefundTournamentInput,
  GenerateBracketInput,
  OrganizerListResponse,
  OrganizerProfile,
  OrganizerProfileStatus,
  RegistrantRosterEntry,
  SeedDivisionInput,
  SuspendTournamentInput,
  TournamentDetail,
  TournamentStatus,
  UpdateTournamentInput,
} from '@bravemove/types';
import { adminFetch, buildQuery } from '../auth/apiClient';

// Re-export the frozen read shapes the pages consume so they can import them
// from this client module (and transparently from the fixture via the alias).
export type {
  AdminAnnouncementItem,
  TournamentDetail,
  OrganizerProfile,
  OrganizerProfileWithAccount,
  RegistrantRosterEntry,
  BracketView,
} from '@bravemove/types';

// ─── Response shapes ─────────────────────────────────────────────────────────

/**
 * The admin list row. The backend `GET /admin/tournaments` returns raw
 * snake_case rows (`SELECT * FROM tournaments`); this client maps the subset the
 * moderation list needs into a stable camelCase shape.
 */
export interface AdminTournamentRow {
  readonly id: string;
  readonly title: string;
  readonly sportName: string;
  readonly status: TournamentStatus;
  readonly organizerB2bAccountId: string;
  readonly organizerKind: string;
  readonly startDate: string;
  readonly endDate: string;
  readonly totalRegisteredCount: number;
  readonly totalCapacity: number | null;
  readonly suspendedAt: string | null;
  readonly cityCode: string | null;
  readonly createdAt: string;
}

export interface AdminTournamentsListResponse {
  readonly data: readonly AdminTournamentRow[];
  readonly pagination: {
    readonly page: number;
    readonly perPage: number;
    readonly total: number;
  };
}

/** Platform analytics rollup — `GET /admin/tournaments/analytics`. */
export interface AdminTournamentAnalyticsResponse {
  readonly byStatus: readonly { readonly status: TournamentStatus; readonly count: number }[];
  readonly bySport: readonly { readonly sportName: string; readonly count: number }[];
  readonly byCity: readonly { readonly cityCode: string | null; readonly count: number }[];
  readonly feeRevenueByMonth: readonly { readonly month: string; readonly feeMinor: number }[];
  readonly registrationsByDay: readonly { readonly day: string; readonly count: number }[];
  readonly suspendedCount: number;
  readonly totalRegistrations: number;
  readonly confirmedRegistrations: number;
}

export interface ListTournamentsOpts {
  /** Single status value; omit (or 'all') for every status. */
  readonly status?: TournamentStatus | 'all';
  /** Sport filter — forwarded server-side (`tournaments.sport_name`, rev-17). */
  readonly sport?: string;
  /** When true, only suspended tournaments; when false, only un-suspended. */
  readonly suspended?: boolean;
  readonly organizerB2bAccountId?: string;
  readonly page?: number;
  readonly perPage?: number;
}

interface DataEnvelope<T> {
  readonly data: T;
}

/**
 * Raw `tournaments` row shape the admin list endpoint returns (`SELECT *`). Only
 * the columns the list maps are typed; the rest are tolerated.
 */
interface RawTournamentRow {
  readonly id: string;
  readonly title: string;
  readonly sport_name: string;
  readonly status: TournamentStatus;
  readonly organizer_b2b_account_id: string;
  readonly organizer_kind: string;
  readonly start_date: string;
  readonly end_date: string;
  readonly total_registered_count: number | string;
  readonly total_capacity: number | string | null;
  readonly suspended_at: string | null;
  /** The real `tournaments` column is `primary_city_code` (NOT `city_code`). */
  readonly primary_city_code: string | null;
  readonly created_at: string;
}

function mapRow(r: RawTournamentRow): AdminTournamentRow {
  return {
    id: r.id,
    title: r.title,
    sportName: r.sport_name,
    status: r.status,
    organizerB2bAccountId: r.organizer_b2b_account_id,
    organizerKind: r.organizer_kind,
    startDate: r.start_date,
    endDate: r.end_date,
    totalRegisteredCount: Number(r.total_registered_count ?? 0),
    totalCapacity: r.total_capacity === null ? null : Number(r.total_capacity),
    suspendedAt: r.suspended_at,
    cityCode: r.primary_city_code ?? null,
    createdAt: r.created_at,
  };
}

function buildListQuery(opts: ListTournamentsOpts): string {
  const params: Record<string, string | number | boolean | undefined | null> = {
    organizerB2bAccountId: opts.organizerB2bAccountId,
    page: opts.page,
    perPage: opts.perPage,
  };
  if (opts.status && opts.status !== 'all') params.status = opts.status;
  if (opts.sport) params.sport = opts.sport;
  if (opts.suspended !== undefined) params.suspended = opts.suspended;
  return buildQuery(params);
}

// ─── API surface ─────────────────────────────────────────────────────────────

export const adminTournamentsApi = {
  /** GET /admin/tournaments — paginated cross-org moderation list. */
  listTournaments: async (
    opts: ListTournamentsOpts = {},
  ): Promise<AdminTournamentsListResponse> => {
    const raw = await adminFetch<{
      data: readonly RawTournamentRow[];
      pagination: { page: number; perPage: number; total: number };
    }>(`/admin/tournaments${buildListQuery(opts)}`);
    return {
      data: raw.data.map(mapRow),
      pagination: raw.pagination,
    };
  },

  /** GET /admin/tournaments/analytics — platform rollup. */
  getAnalytics: async (): Promise<AdminTournamentAnalyticsResponse> => {
    const body = await adminFetch<DataEnvelope<Partial<AdminTournamentAnalyticsResponse>>>(
      `/admin/tournaments/analytics`,
    );
    const d = body.data;
    return {
      byStatus: d.byStatus ?? [],
      bySport: d.bySport ?? [],
      byCity: d.byCity ?? [],
      feeRevenueByMonth: d.feeRevenueByMonth ?? [],
      registrationsByDay: d.registrationsByDay ?? [],
      suspendedCount: d.suspendedCount ?? 0,
      totalRegistrations: d.totalRegistrations ?? 0,
      confirmedRegistrations: d.confirmedRegistrations ?? 0,
    };
  },

  /** GET /admin/tournaments/:id — full detail (divisions + hosts). */
  getTournament: async (id: string): Promise<TournamentDetail | null> => {
    try {
      const body = await adminFetch<DataEnvelope<TournamentDetail>>(
        `/admin/tournaments/${encodeURIComponent(id)}`,
      );
      return body.data;
    } catch (err) {
      if ((err as { status?: number }).status === 404) return null;
      throw err;
    }
  },

  /**
   * GET /admin/tournaments/:id/bracket — the ADMIN in-play bracket read
   * (rev-08/rev-09). Returns ONE `BracketView` PER DIVISION; the page renders
   * one tree per entry. Bypasses the invite-only viewer gate server-side
   * (`tournaments:read`), so moderators can inspect invite-only tournaments.
   * Returns null when the tournament 404s OR no division has any bracket
   * structure yet (empty array → null, the page's no-bracket empty state).
   */
  getBracket: async (id: string): Promise<readonly BracketView[] | null> => {
    try {
      const body = await adminFetch<DataEnvelope<readonly BracketView[]>>(
        `/admin/tournaments/${encodeURIComponent(id)}/bracket`,
      );
      return body.data.length > 0 ? body.data : null;
    } catch (err) {
      if ((err as { status?: number }).status === 404) return null;
      throw err;
    }
  },

  /**
   * GET /admin/tournaments/:id/announcements — every announcement of the
   * tournament, hidden ones included (with `hiddenAt` / `hiddenReason`).
   * Readers never see a hidden one; this is the moderation view. FIX 5.7.
   */
  listAnnouncements: async (id: string): Promise<readonly AdminAnnouncementItem[]> => {
    const body = await adminFetch<DataEnvelope<readonly AdminAnnouncementItem[]>>(
      `/admin/tournaments/${encodeURIComponent(id)}/announcements`,
    );
    return body.data;
  },

  /**
   * POST /admin/tournament-announcements/:id/hide — hide an announcement from
   * every reader (F43). The reason is mandatory and audited. Answers 204.
   */
  hideAnnouncement: async (announcementId: string, reason: string): Promise<void> => {
    await adminFetch<unknown>(
      `/admin/tournament-announcements/${encodeURIComponent(announcementId)}/hide`,
      { method: 'POST', body: { reason } },
    );
  },

  /**
   * PATCH /admin/tournaments/:id — admin edit (title + description only; the
   * standard organizer field-lock still applies server-side — MN2).
   */
  editTournament: async (id: string, input: UpdateTournamentInput): Promise<TournamentDetail> => {
    const body = await adminFetch<DataEnvelope<TournamentDetail>>(
      `/admin/tournaments/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: input as unknown as Record<string, unknown> },
    );
    return body.data;
  },

  /** POST /admin/tournaments/:id/suspend — orthogonal admin suspend flag. */
  suspendTournament: async (
    id: string,
    input: SuspendTournamentInput,
  ): Promise<{ readonly id: string; readonly suspended: boolean }> => {
    const body = await adminFetch<DataEnvelope<{ id: string; suspended: boolean }>>(
      `/admin/tournaments/${encodeURIComponent(id)}/suspend`,
      { method: 'POST', body: input as unknown as Record<string, unknown> },
    );
    return body.data;
  },

  /** POST /admin/tournaments/:id/unsuspend — clear the suspension. */
  unsuspendTournament: async (
    id: string,
  ): Promise<{ readonly id: string; readonly suspended: boolean }> => {
    const body = await adminFetch<DataEnvelope<{ id: string; suspended: boolean }>>(
      `/admin/tournaments/${encodeURIComponent(id)}/unsuspend`,
      { method: 'POST' },
    );
    return body.data;
  },

  /** POST /admin/tournaments/:id/cancel — cancel + refund cascade. */
  /**
   * POST /admin/tournaments/:id/publish — publish a draft on the organizer's
   * behalf (gate + quota bypassed, listing fee waived). FIX 4.4 (F8).
   */
  publishTournament: async (id: string, input: { reason: string }): Promise<TournamentDetail> => {
    const body = await adminFetch<DataEnvelope<TournamentDetail>>(
      `/admin/tournaments/${encodeURIComponent(id)}/publish`,
      { method: 'POST', body: input as unknown as Record<string, unknown> },
    );
    return body.data;
  },

  cancelTournament: async (id: string, input: CancelTournamentInput): Promise<TournamentDetail> => {
    const body = await adminFetch<DataEnvelope<TournamentDetail>>(
      `/admin/tournaments/${encodeURIComponent(id)}/cancel`,
      { method: 'POST', body: input as unknown as Record<string, unknown> },
    );
    return body.data;
  },

  /** POST /admin/tournaments/:id/force-refund — flag all paid registrations refunded. */
  forceRefund: async (
    id: string,
    input: ForceRefundTournamentInput,
  ): Promise<{ readonly tournamentId: string; readonly refundedCount: number }> => {
    const body = await adminFetch<DataEnvelope<{ tournamentId: string; refundedCount: number }>>(
      `/admin/tournaments/${encodeURIComponent(id)}/force-refund`,
      {
        method: 'POST',
        body: input as unknown as Record<string, unknown>,
      },
    );
    return body.data;
  },

  /**
   * POST /admin/tournaments — admin-create on behalf of an organizer (D19).
   * Bypasses the Pro-tier gate + waives the listing fee at publish; mandatory
   * audited reason. Gated server-side on `tournaments:create`.
   */
  createTournament: async (input: AdminCreateTournamentInput): Promise<TournamentDetail> => {
    const body = await adminFetch<DataEnvelope<TournamentDetail>>('/admin/tournaments', {
      method: 'POST',
      body: input as unknown as Record<string, unknown>,
    });
    return body.data;
  },

  /**
   * POST /admin/tournaments/:id/matches/:matchId/score — admin draft score
   * (`tournaments:enter_scores`). The page refetches the bracket after.
   */
  enterScore: async (
    tournamentId: string,
    matchId: string,
    input: EnterMatchScoreInput,
  ): Promise<unknown> => {
    const body = await adminFetch<DataEnvelope<unknown>>(
      `/admin/tournaments/${encodeURIComponent(tournamentId)}/matches/${encodeURIComponent(matchId)}/score`,
      { method: 'POST', body: input as unknown as Record<string, unknown> },
    );
    return body.data;
  },

  /**
   * POST /admin/tournaments/:id/matches/:matchId/finalize — admin finalize
   * (one-way; advances the bracket + feeds F31). `tournaments:enter_scores`.
   */
  finalizeMatch: async (
    tournamentId: string,
    matchId: string,
    input: FinalizeMatchInput,
  ): Promise<unknown> => {
    const body = await adminFetch<DataEnvelope<unknown>>(
      `/admin/tournaments/${encodeURIComponent(tournamentId)}/matches/${encodeURIComponent(matchId)}/finalize`,
      { method: 'POST', body: input as unknown as Record<string, unknown> },
    );
    return body.data;
  },

  /**
   * POST /admin/tournaments/:id/matches/:matchId/correct — admin post-final
   * correction (mandatory reason; audited). `tournaments:enter_scores`.
   */
  correctScore: async (
    tournamentId: string,
    matchId: string,
    input: CorrectMatchScoreInput,
  ): Promise<unknown> => {
    const body = await adminFetch<DataEnvelope<unknown>>(
      `/admin/tournaments/${encodeURIComponent(tournamentId)}/matches/${encodeURIComponent(matchId)}/correct`,
      { method: 'POST', body: input as unknown as Record<string, unknown> },
    );
    return body.data;
  },

  /**
   * POST /admin/tournaments/:id/divisions/:divId/seed — admin seed
   * (`tournaments:manage_bracket`).
   */
  seedDivision: async (
    tournamentId: string,
    divisionId: string,
    input: SeedDivisionInput,
  ): Promise<unknown> => {
    const body = await adminFetch<DataEnvelope<unknown>>(
      `/admin/tournaments/${encodeURIComponent(tournamentId)}/divisions/${encodeURIComponent(divisionId)}/seed`,
      { method: 'POST', body: input as unknown as Record<string, unknown> },
    );
    return body.data;
  },

  /**
   * POST /admin/tournaments/:id/divisions/:divId/bracket — admin
   * generate-bracket (`tournaments:manage_bracket`).
   */
  generateBracket: async (
    tournamentId: string,
    divisionId: string,
    input: GenerateBracketInput = { twoStage: false },
  ): Promise<unknown> => {
    const body = await adminFetch<DataEnvelope<unknown>>(
      `/admin/tournaments/${encodeURIComponent(tournamentId)}/divisions/${encodeURIComponent(divisionId)}/bracket`,
      { method: 'POST', body: input as unknown as Record<string, unknown> },
    );
    return body.data;
  },

  /** POST /admin/organizers/:b2bAccountId/approve — approve an organizer partner. */
  approveOrganizer: async (
    b2bAccountId: string,
    input: ApproveOrganizerInput,
  ): Promise<OrganizerProfile> => {
    const body = await adminFetch<DataEnvelope<OrganizerProfile>>(
      `/admin/organizers/${encodeURIComponent(b2bAccountId)}/approve`,
      { method: 'POST', body: input as unknown as Record<string, unknown> },
    );
    return body.data;
  },

  /** POST /admin/organizers/:b2bAccountId/suspend — suspend an organizer partner. */
  suspendOrganizer: async (b2bAccountId: string, reason: string): Promise<OrganizerProfile> => {
    const body = await adminFetch<DataEnvelope<OrganizerProfile>>(
      `/admin/organizers/${encodeURIComponent(b2bAccountId)}/suspend`,
      { method: 'POST', body: { reason } },
    );
    return body.data;
  },

  /**
   * GET /admin/organizers — the pending-approval queue (paginated, joined to the
   * b2b_account legal/display name). Filter by `status` (default: all).
   */
  listOrganizers: async (
    opts: {
      status?: OrganizerProfileStatus;
      cursor?: string | null;
      limit?: number;
    } = {},
  ): Promise<OrganizerListResponse> => {
    const qs = buildQuery({
      status: opts.status,
      cursor: opts.cursor ?? undefined,
      limit: opts.limit,
    });
    return adminFetch<OrganizerListResponse>(`/admin/organizers${qs}`);
  },

  /**
   * GET /admin/tournaments/:id/registrants — the real roster (each registration
   * joined to its division name + registrant identity). Returns [] on 404.
   */
  getRegistrants: async (id: string): Promise<readonly RegistrantRosterEntry[]> => {
    try {
      const body = await adminFetch<DataEnvelope<readonly RegistrantRosterEntry[]>>(
        `/admin/tournaments/${encodeURIComponent(id)}/registrants`,
      );
      return body.data;
    } catch (err) {
      if ((err as { status?: number }).status === 404) return [];
      throw err;
    }
  },
};
